import isEqual from 'lodash/isEqual';
import { useContext, useEffect } from 'react';
import type { SidePanelSubject } from '../../types';
import { SidePanelOwnerContext } from '../contexts/SidePanelOwnerContext';
import { useSidePanelCommands } from './useSidePanelCommands';
import { useSidePanelState } from './useSidePanelState';

/**
 * Refreshes the active session's subject when its declaring owner receives changed entity data.
 *
 * @param subject - Current subject; null leaves the session unchanged.
 * @remarks
 * Requires SidePanelSystemProvider and DeepLinkProvider. Updates only a session owned and declared by the
 * current SidePanelOwner, and only when the subject differs by deep equality. Does not open or close panels.
 */
export function useSidePanelSubjectSync(subject: SidePanelSubject | null) {
    const owner = useContext(SidePanelOwnerContext);
    const session = useSidePanelState((state) => state.session);
    const { setSubject } = useSidePanelCommands();
    const ownsSession = Boolean(
        owner &&
        session?.ownerId === owner.ownerId &&
        owner.ownerRegistrations.some((entry) =>
            entry.declarations.some(({ definition }) => definition.id === session?.panelId)
        )
    );

    useEffect(() => {
        if (ownsSession && subject && !isEqual(session?.subject, subject)) {
            setSubject(subject);
        }
    }, [ownsSession, session?.sessionId, session?.subject, setSubject, subject]);
}
